import { jsonFetch } from '@/lib/api/jsonFetch'
import type {
  ConfirmResponse,
  ProcessResponse,
  VendorCandidate,
  VendorProductsResponse,
} from './types'

/** POST /cofa/process — multipart. Accepts optional user-picked vendor
 *  to retry after a `needs_vendor` response. */
export async function processCofa(
  file: File,
  opts: { vendorId?: number; purchaseOrderId?: number } = {},
): Promise<ProcessResponse> {
  const form = new FormData()
  form.append('file', file, file.name)
  if (opts.vendorId !== undefined) form.append('vendor_id', String(opts.vendorId))
  if (opts.purchaseOrderId !== undefined)
    form.append('purchase_order_id', String(opts.purchaseOrderId))
  return jsonFetch<ProcessResponse>('/api/procurement/cofa/process', {
    method: 'POST',
    rawBody: form,
  })
}

/** POST /cofa/confirm — apply a user-confirmed plan. */
export async function confirmCofa(plan: unknown): Promise<ConfirmResponse> {
  return jsonFetch<ConfirmResponse>('/api/procurement/cofa/confirm', {
    method: 'POST',
    body: { plan },
  })
}

/** POST /cofa/learning/{id}/revoke — "Wrong?": stop using this learned
 *  match, for everyone. Logged server-side. */
export async function revokeLearned(aliasId: number): Promise<{ ok: boolean }> {
  return jsonFetch<{ ok: boolean }>(`/api/procurement/cofa/learning/${aliasId}/revoke`, {
    method: 'POST',
  })
}

/** GET /vendors — commercial-entity vendor autocomplete, already
 *  collapsed by `commercial_partner_id` server-side. */
export async function searchVendors(q: string): Promise<VendorCandidate[]> {
  const qs = q.trim() ? `?q=${encodeURIComponent(q.trim())}` : ''
  const resp = await jsonFetch<{ results: VendorCandidate[] }>(
    `/api/procurement/vendors${qs}`,
  )
  return resp.results ?? []
}

/** GET /vendor-products — products linked to a vendor via supplierinfo,
 *  plus optional catalog search expansion. Drives the confirm-view's
 *  per-lot product picker when the auto-match fails. */
export async function getVendorProducts(
  vendorId: number,
  q: string = '',
  printed?: string,
): Promise<VendorProductsResponse> {
  const params = new URLSearchParams({ vendor_id: String(vendorId) })
  if (q.trim()) params.set('q', q.trim())
  // The product text on the CofA: the server puts products the team already
  // matched to it first.
  if (printed?.trim()) params.set('printed', printed.trim())
  return jsonFetch<VendorProductsResponse>(
    `/api/procurement/vendor-products?${params.toString()}`,
  )
}
